import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';

import errorData from './errorData';

import Modal from '../../../components/Modal';
import Icon from '../../../components/Icons';
import Accordion from '../../../components/Accordion';
import FormContainer from '../../../components/FormContainer';
import DatepickerMui from '../../../components/Inputs/DatepickerMui';
import DataTableMui from '../../../components/DataTableMui';
import Button from '../../../components/Button';
import Select from '../../../components/Inputs/Select';
import Radio from '../../../components/Inputs/Radio';
import PageLoader from '../../../components/PageLoader';

import { useGlobal } from '../../../contexts/Global/context';

import { getRelatorioVidas, gerarBateCadastral, downloadBateCadastral } from '../../../services';

import { downloadFile, cloneObject, translateContractsGroupsListToOptions } from '../../../utils';
import validateForm from '../../../utils/validateForm';

import filterSchema from './schema';

import { PageContainer, ContainerButton } from '../../../styles/styles';
import * as S from './styles';

function Relatorio() {
    const { dataActiveContract, contractsGroupsList, contrastTheme } = useGlobal();

    const emptyMessage = 'Nenhum relatório gerado';
    const DATE_FORMAT = 'DD/MM/YYYY';

    const [allFieldsValidated, setAllFieldsValidated] = useState(false);
    const [enableClearSelection, setEnableClearSelection] = useState(true);
    const [pageLoading, setPageLoading] = useState(false);
    const [showModalDeleteFile, setShowModalDeleteFile] = useState(false);
    const [showModalError, setShowModalError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showingModalBateCadastral, setShowingModalBateCadastral] = useState(false);
    const [modalBateCadastralData, setModalBateCadastralData] = useState({});
    const [dataTable, setDataTable] = useState([]);
    const [indexFileToBeDeleted, setIndexFileToBeDeleted] = useState(-1);
    const [newId, setNewId] = useState(0);
    const [filterValues, setFilterValues] = useState({
        ...cloneObject(filterSchema),
        contracts: {
            ...filterSchema.contracts,
            value: [dataActiveContract.numero],
        },
    });

    const adminGroups =
        contractsGroupsList
            ?.filter((group) =>
                group.contratos?.some(
                    (contract) => contract.contratoResponsavelGrupo && contract.classificacaoContatoRH
                )
            )
            .map((group) => ({
                ...group,
                codigo: `${group.idGrupoContrato}`,
                nome: `${group.idGrupoContrato} - ${group.grupoContrato}`,
            })) || [];

    async function handleConfirmDeleteFile() {
        const filteredDataTable = dataTable.filter(({ id: idTable }) => idTable !== indexFileToBeDeleted);

        setDataTable(filteredDataTable);
    }

    async function handleDeleteFile(id) {
        setIndexFileToBeDeleted(id);

        setShowModalDeleteFile(true);
    }

    async function handleDownFile({ arquivoRelatorio, dtEmissao, numContrato, base64Header, ...rest }) {
        const { idArquivoBateCadastral } = rest;

        const formattedDate = moment(dtEmissao).format('DD_MM_YYYY');

        let base64BateCadastral;
        let nomeBateCadastral;

        if (idArquivoBateCadastral) {
            const date = moment().format('YYYY-MM-DD');

            setPageLoading(true);

            const { data } = (await downloadBateCadastral(date, date, idArquivoBateCadastral)) || {
                data: [{ errorCode: -1 }],
            };

            const { nome, arquivo } = data || {};

            base64BateCadastral = arquivo;

            nomeBateCadastral = nome;

            setPageLoading(false);

            if (!arquivo || !nome) {
                const { errorCode } = data[0];

                setModalBateCadastralData(errorData[errorCode || -1]);

                setShowingModalBateCadastral(true);

                return;
            }
        }

        const createdFileName = nomeBateCadastral || `relatorio_${numContrato}_${formattedDate}`;

        downloadFile(createdFileName, `data:${base64Header};base64,${base64BateCadastral || arquivoRelatorio}`);
    }

    const columnsHead = [
        {
            key: 'dtEmissao',
            selector: (row) => (row.dtEmissao ? moment(row.dtEmissao).format(DATE_FORMAT) : '-'),
            name: 'Emissão',
            sortable: true,
            direction: 'left',
            customStyles: () => ({ width: '100px' }),
        },
        {
            key: 'numContrato',
            selector: (row) => row.numContrato || '-',
            name: 'Contrato',
            sortable: true,
            direction: 'left',
            customStyles: () => ({ width: '150px' }),
        },
        {
            key: 'grupoContrato',
            selector: (row) => row.grupoContrato || '-',
            name: 'Grupo de Contrato',
            sortable: true,
            direction: 'left',
            customStyles: () => ({ width: '150px' }),
        },
        {
            key: 'nomeEmpresa',
            selector: (row) => row.nomeEmpresa || '-',
            name: 'Empresa',
            sortable: true,
            direction: 'left',
            customStyles: () => ({ width: '400px' }),
        },
        {
            key: 'tipoRelatorio',
            selector: (row) => row.tipoRelatorio || '-',
            name: 'Tipo de relatório',
            sortable: true,
            direction: 'left',
            customStyles: () => ({ width: '300px' }),
        },
        {
            key: 'arquivoRelatorio',
            selector: (row) => row,
            name: '',
            sortable: false,
            direction: 'left',
            mutation: ({ arquivoRelatorio, idArquivoBateCadastral, ...rest }) => (
                <S.CellWithIcon onClick={() => handleDownFile({ arquivoRelatorio, idArquivoBateCadastral, ...rest })}>
                    {(arquivoRelatorio || idArquivoBateCadastral) && (
                        <Icon name="file_download" tooltip="Baixar" size={16} />
                    )}
                    {(arquivoRelatorio || idArquivoBateCadastral) && (
                        <span
                            style={{
                                color: contrastTheme ? '#FFF333' : '#EC6E0C',
                                fontWeight: 'bold',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                            }}
                        >
                            Baixar
                        </span>
                    )}
                </S.CellWithIcon>
            ),
            customStyles: ({ arquivoRelatorio }) => ({ width: '200px', cursor: arquivoRelatorio && 'pointer' }),
        },
        {
            key: 'delete',
            selector: ({ tipoRelatorio, id }) =>
                tipoRelatorio && (
                    <Icon
                        color={contrastTheme ? '#FFF333' : '#EC6E0C'}
                        name="delete"
                        tooltip="Deletar"
                        onClick={() => handleDeleteFile(id)}
                        size={16}
                    />
                ),
            name: '',
            direction: 'left',
            customStyles: ({ tipoRelatorio }) => ({ width: '100px', cursor: tipoRelatorio && 'pointer' }),
        },
    ];

    async function handleGenerateReport() {
        setLoading(true);

        setNewId(newId + 1);

        let isContractGroup = false;

        if (filterValues.tipoSelecao.value === '2') isContractGroup = true;
        const contractList = isContractGroup ? filterValues.contractsGroup.value : filterValues.contracts.value;

        const listRequests = contractList.map(async (contractNumber) => {
            const typeFile = filterValues.tipoRelatorio.value;
            const statusFile = filterValues.status.value;

            let base64;
            let idArquivoBateCadastral;

            // Bate cadastral
            if (typeFile === '3') {
                const params = {
                    date: moment(filterValues.date.value).format('YYYY-MM-DD'),
                    contract: contractNumber,
                    contractParam: isContractGroup ? 'grupoContrato' : 'numContrato',
                    status: statusFile !== 'Ambas' ? statusFile : null,
                };

                const {
                    data: { idArquivo },
                } = (await gerarBateCadastral(params.date, params.contract, params.contractParam, params.status)) || {};

                idArquivoBateCadastral = idArquivo;
            }

            // Extrato PDF
            if (typeFile === '1') {
                const { arquivo } =
                    (await getRelatorioVidas(contractNumber, {
                        situacaoBeneficiario: filterValues.status.value,
                        dataPosicaoBeneficiario: moment(filterValues.date.value).format('YYYY-MM-DD'),
                    })) || {};

                base64 = arquivo;
            }

            const contratos = contractsGroupsList.map((groupContract) => groupContract.contratos);

            const contrato = { nomeContrato: ' - ' };

            contratos.forEach((c) => {
                c.forEach((contr) => {
                    if (contr.numeroContrato === contractNumber) {
                        contrato.nomeContrato = contr.nomeContrato;
                    }
                });
            });

            const item = {
                arquivoRelatorio: base64,
                base64File: base64,
                idArquivoBateCadastral,
                base64Header: filterValues.tipoRelatorio.base64Header,
                nomeEmpresa: contrato.nomeContrato,
                dtEmissao: filterValues.date.value,
                id: newId,
                tipoRelatorio: filterValues.tipoRelatorio.lookup,
            };

            item[isContractGroup ? 'grupoContrato' : 'numContrato'] = contractNumber;

            return { ...item };
        }, []);

        const resultList = await Promise.all(listRequests);

        const treatedResults = resultList.filter(
            ({ arquivoRelatorio, idArquivoBateCadastral }) => arquivoRelatorio || idArquivoBateCadastral
        );

        if (resultList.length !== treatedResults.length) setShowModalError(true);

        setDataTable([...dataTable, ...treatedResults]);

        setLoading(false);
    }

    function validateFields(form) {
        const { isValid } = validateForm(form);

        return isValid;
    }

    async function handleChangeInput(event) {
        const { id, value, ...rest } = event;

        const field = { ...filterValues[id], value, error: '', ...rest };

        const editedForm = { ...filterValues, [id]: field };

        if (id === 'tipoSelecao') {
            editedForm.contracts.required = value === '1';
            editedForm.contractsGroup.required = value === '2';
            editedForm.tipoRelatorio.options[0].disabled = value === '2';
            if (value === '2') {
                editedForm.tipoRelatorio.value = '3';
                editedForm.tipoRelatorio.lookup = 'Bate cadastral(.txt)';
            }
        }

        const allFieldsFilled = validateFields(editedForm);

        setAllFieldsValidated(allFieldsFilled);

        setFilterValues(editedForm);
    }

    function handleClearContracts() {
        if (filterValues.tipoSelecao.value === '1') {
            if (filterValues.contracts?.value?.length > 0) {
                handleChangeInput({ ...filterValues.contracts, value: [] });
                return;
            }
        }
        if (filterValues.contractsGroup?.value?.length > 0) {
            handleChangeInput({ ...filterValues.contractsGroup, value: [] });
        }
    }

    const handleClickClearAll = useCallback(() => {
        setLoading(true);
        setDataTable([]);
        setLoading(false);
    }, [dataTable]);

    const rowMutation = {
        click: () => ({}),
        hasClick: () => true,
    };

    useEffect(() => {
        setEnableClearSelection(
            (filterValues.tipoSelecao.value === '1' && filterValues.contracts.value?.length > 0) ||
                (filterValues.tipoSelecao.value === '2' && filterValues.contractsGroup.value?.length > 0)
        );
    }, [filterValues]);

    return (
        <>
            <PageContainer style={{ paddingLeft: 32, paddingRight: 32 }}>
                <Accordion title="Pesquisar beneficiários por data específicado contrato" hasCollapse={false}>
                    <p style={{ width: '100%' }}>
                        <S.TitleReport>
                            Você poderá obter extrato dos beneficiários nos formatos PDF ou .txt e o
                            <b> bate cadastral será disponível apenas no formato .txt</b>
                        </S.TitleReport>
                    </p>

                    {adminGroups.length > 0 && <Radio field={filterValues.tipoSelecao} onChange={handleChangeInput} />}
                    <FormContainer>
                        <DatepickerMui field={filterValues.date} onChange={handleChangeInput} />

                        <S.SelectContainer style={{ flexDirection: 'column', justifyContent: 'end' }}>
                            {filterValues.tipoSelecao.value === '2' && (
                                <Select
                                    multiple
                                    field={{
                                        ...filterValues.contractsGroup,
                                        options: adminGroups,
                                    }}
                                    onChange={handleChangeInput}
                                />
                            )}
                            {filterValues.tipoSelecao.value === '1' && (
                                <Select
                                    multiple
                                    field={{
                                        ...filterValues.contracts,
                                        options: translateContractsGroupsListToOptions(contractsGroupsList),
                                    }}
                                    onChange={handleChangeInput}
                                />
                            )}
                            <S.LinkButton
                                id="clearContracts"
                                onClick={handleClearContracts}
                                style={{ marginTop: '-14px' }}
                            >
                                <span className={enableClearSelection ? 'active' : ''}>Limpar seleção</span>
                            </S.LinkButton>
                        </S.SelectContainer>

                        <Radio field={filterValues.status} onChange={handleChangeInput} />

                        <S.TipoDeRelatorioContainer>
                            <Radio field={filterValues.tipoRelatorio} onChange={handleChangeInput} />

                            <ContainerButton>
                                <S.ButtonsWrap>
                                    <Button
                                        disabled={!allFieldsValidated || loading}
                                        loading={loading}
                                        id="generateReport"
                                        type="primary"
                                        onClick={handleGenerateReport}
                                    >
                                        Gerar {dataTable.length > 0 && 'novo'} relatório
                                    </Button>
                                </S.ButtonsWrap>
                            </ContainerButton>
                        </S.TipoDeRelatorioContainer>
                    </FormContainer>
                </Accordion>

                {dataTable.length > 0 && (
                    <S.LinkButton id="clearRegisters" onClick={handleClickClearAll}>
                        <span className="active">Limpar registros</span>
                    </S.LinkButton>
                )}

                <DataTableMui
                    rowMutation={rowMutation}
                    data={dataTable}
                    emptyMessage={emptyMessage}
                    columns={columnsHead}
                    alreadySearched={dataTable.length > 0}
                    firstOrderBy="numContrato"
                />
            </PageContainer>

            <Modal
                title="Atenção"
                subtitle="Deseja realmente deletar este arquivo?"
                visible={showModalDeleteFile}
                onCancel={() => setShowModalDeleteFile(false)}
                footer={null}
            >
                <Button
                    type="secondary"
                    onClick={() => {
                        setShowModalDeleteFile(false);
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    type="primary"
                    onClick={() => {
                        handleConfirmDeleteFile();
                        setShowModalDeleteFile(false);
                    }}
                >
                    Deletar
                </Button>
            </Modal>

            <Modal
                title={modalBateCadastralData.title}
                subtitle={modalBateCadastralData.content}
                visible={showingModalBateCadastral}
                onCancel={() => setShowingModalBateCadastral(false)}
                footer={null}
            >
                <Button type="primary" onClick={() => setShowingModalBateCadastral(false)}>
                    Entendi
                </Button>
            </Modal>

            <Modal
                title="Atenção"
                subtitle="Nenhuma informação foi encontrada para um ou mais dos contratos pesquisados com o critério de seleção estabelecido."
                visible={showModalError}
                onCancel={() => setShowModalError(false)}
                footer={null}
            >
                <Button type="primary" onClick={() => setShowModalError(false)}>
                    Entendi
                </Button>
            </Modal>

            <PageLoader loading={pageLoading} />
        </>
    );
}

export default Relatorio;
